// My Account: the signed-in person's own profile. Name is editable, the email is shown read-only, and the password can be
// changed -- by asking for the current one first, so a left-open desk cannot have its password taken over.
// For the admin role it edits the first active admin; for the others, the account whose name matches "Signed in as".
import { useState } from 'react';

import { Fact, Notice } from 'src/front-components/lead-desk/kit';
import { Field } from 'src/front-components/lead-desk/student-form';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { ShellCard } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput, ErrorLine } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import { useLiveData } from 'src/front-components/utils/desk-data';
import { fetchUsers, updateUser } from 'src/front-components/utils/lead-desk-store';
import { accountFor, validateProfile, type ProfileForm } from 'src/utils/lead-desk';

export const AccountScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  // A signed-in person edits their OWN account; only the CRM chooser has to look the account up by name. (Fetching all users is admin-only.)
  const users = useLiveData(ctx.identity === null ? fetchUsers : () => Promise.resolve([]), 60_000);
  const mine = ctx.identity === null ? null : { id: ctx.identity.id, firstName: ctx.identity.firstName, lastName: ctx.identity.lastName, email: ctx.identity.email, role: ctx.identity.role, isActive: true };
  const account = mine ?? accountFor(users.data ?? [], ctx.role, ctx.person);
  const [draft, setDraft] = useState<(ProfileForm & { current: string; confirm: string; for: string }) | null>(null);
  const [errors, setErrors] = useState<Partial<Record<'firstName' | 'lastName' | 'password' | 'current' | 'confirm', string>>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // Start the form from the account once it loads (and again if a different person is chosen).
  const form =
    draft !== null && draft.for === account?.id
      ? draft
      : { firstName: account?.firstName ?? '', lastName: account?.lastName ?? '', password: '', current: '', confirm: '', for: account?.id ?? '' };
  const set = (patch: Partial<typeof form>): void => setDraft({ ...form, ...patch });

  const save = async (): Promise<void> => {
    if (account === null) return;

    const found: typeof errors = { ...validateProfile(form) };

    if (form.password !== '' && form.current === '') found.current = 'Enter your current password to change it.';
    if (form.password !== form.confirm) found.confirm = 'The two passwords do not match.';

    setErrors(found);
    setSaved(null);
    setFailure(null);

    if (Object.keys(found).length > 0) return;

    setBusy(true);

    try {
      // The server checks the current password itself; the browser never gets to say it was right.
      await updateUser(account.id, { firstName: form.firstName, lastName: form.lastName, ...(form.password === '' ? {} : { password: form.password, currentPassword: form.current }) });
      await Promise.all([users.refresh(), ctx.refresh(), ctx.identity?.refresh() ?? Promise.resolve()]);
      setDraft(null);
      setSaved(form.password === '' ? 'Your profile was saved.' : 'Your profile and password were saved. Your other sessions were signed out.');
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Could not save your profile.';

      if (/current password/i.test(message)) setErrors({ current: message });
      else setFailure(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: SHELL_SPACE[4] }}>
        <div style={{ ...SHELL_TYPE.pageTitle, color: shell.text }}>My Account</div>
        <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>Change your profile details and password</div>
      </div>

      <ShellCard scheme={ctx.scheme}>
        {saved !== null && <Notice tone="green">{saved}</Notice>}
        {account === null ? (
          <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary }}>
            {users.loading ? 'Loading…' : `There is no desk account for ${ctx.person === '' ? 'this role' : ctx.person} yet. Add one in Manage Users and it will appear here.`}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: SHELL_SPACE[3] }}>
              <Field scheme={ctx.scheme} label="First Name" required error={errors.firstName}>
                <DeskInput scheme={ctx.scheme} value={form.firstName} onChange={(firstName) => set({ firstName })} />
              </Field>
              <Field scheme={ctx.scheme} label="Last Name" required error={errors.lastName}>
                <DeskInput scheme={ctx.scheme} value={form.lastName} onChange={(lastName) => set({ lastName })} />
              </Field>
            </div>
            <Fact scheme={ctx.scheme} label="Email (Read-only)" value={account.email} />
            <div style={{ height: SHELL_SPACE[3] }} />
            <Field scheme={ctx.scheme} label="Current Password" error={errors.current}>
              <DeskInput scheme={ctx.scheme} value={form.current} onChange={(current) => set({ current })} placeholder="Only needed to change your password" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: SHELL_SPACE[3] }}>
              <Field scheme={ctx.scheme} label="New Password (leave blank to keep current)" error={errors.password}>
                <DeskInput scheme={ctx.scheme} value={form.password} onChange={(password) => set({ password })} placeholder="Enter new password" />
              </Field>
              <Field scheme={ctx.scheme} label="Confirm New Password" error={errors.confirm}>
                <DeskInput scheme={ctx.scheme} value={form.confirm} onChange={(confirm) => set({ confirm })} />
              </Field>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[3] }}>
              <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void save()}>
                {busy ? 'Saving…' : 'Save Changes'}
              </DeskButton>
              <ErrorLine scheme={ctx.scheme} message={failure ?? users.error} />
            </div>
          </>
        )}
      </ShellCard>
    </div>
  );
};
